import { getParameterByName } from "../../utils";
import ChecklistItemStatus from "models/checklist/checklist-item/checklist-item-status";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import PaxSession from "models/session/pax-session";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";

// ── Types ──────────────────────────────────────────────────────────────────

const BRIDGE_VERSION = "1";

type BridgeEventType =
  | "flowReady"
  | "documentSaved"
  | "paxReadyToFly"
  | "allPaxReadyToFly"
  | "closeFlow"
  | "openExternalLink"
  | "error";

export type CloseFlowReason =
  | "user_closed"
  | "ready_to_fly"
  | "ineligible"
  | "not_found"
  | "session_locked"
  | "outage";

interface ChecklistEntry {
  itemType: string;
  status: "NOT_YET_RESOLVED" | "VERIFICATION_REQUIRED" | "RESOLVED";
}

interface BridgeMessage {
  type: BridgeEventType;
  payload: Record<string, unknown>;
  version: string;
}

// ── Platform bridge helpers ────────────────────────────────────────────────

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        zamna?: {
          postMessage: (msg: BridgeMessage) => void;
        };
      };
    };
    ZamnaBridge?: {
      postMessage: (json: string) => void;
    };
  }
}

// ── Service ────────────────────────────────────────────────────────────────

class NativeBridge {
  static shared = new NativeBridge();

  private _isEmbedded: boolean;

  private constructor() {
    this._isEmbedded = getParameterByName("mode") === "embedded";
  }

  /** Re-check the URL (called once during app init if needed). */
  refreshMode(): void {
    if (!this._isEmbedded) {
      this._isEmbedded = getParameterByName("mode") === "embedded";
    }
  }

  get isEmbedded(): boolean {
    return this._isEmbedded;
  }

  // ── Outgoing events (Web → Native) ──────────────────────────────────

  flowReady(sessionId: string): void {
    this.send("flowReady", { sessionId });
  }

  documentSaved(paxSessionId: string, itemType: string): void {
    this.send("documentSaved", { paxSessionId, itemType });
  }

  paxReadyToFly(paxSessionId: string, paxSession: PaxSession): void {
    const checklist: ChecklistEntry[] = paxSession.checklist.items
      .filter((item) => item instanceof DocumentRequirement)
      .map((item) => ({
        itemType: item.itemType,
        status: mapItemStatus(item.status),
      }));

    this.send("paxReadyToFly", { paxSessionId, checklist });
  }

  allPaxReadyToFly(sessionId: string): void {
    this.send("allPaxReadyToFly", { sessionId });
  }

  closeFlow(reason: CloseFlowReason): void {
    this.send("closeFlow", { reason });
  }

  openExternalLink(url: string): void {
    this.send("openExternalLink", { url });
  }

  bridgeError(code: string, message: string): void {
    this.send("error", { code, message });
  }

  // ── Incoming events (Native → Web) ──────────────────────────────────

  /**
   * Register a handler for backButton events from the native shell.
   * Returns a cleanup function to remove the listener.
   */
  onBackButton(handler: () => void): () => void {
    const listener = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== "object") return;
      if (event.data.type === "backButton") {
        handler();
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }

  // ── Helpers for detecting pax/session RTF transitions ───────────────

  /**
   * Call after pax sessions are updated.
   * Compares previous vs current state and emits documentSaved,
   * paxReadyToFly, and allPaxReadyToFly as needed.
   */
  emitDocumentAndReadyEvents(
    sessionId: string,
    previousPaxSessions: PaxSession[],
    updatedPaxSessions: PaxSession[]
  ): void {
    if (!this._isEmbedded) return;

    for (const updated of updatedPaxSessions) {
      // No match = never observed before; treat as "not ready before" rather than skip.
      const previous = previousPaxSessions.find(
        (p) => p.paxSessionId === updated.paxSessionId
      );

      // Emit documentSaved for each item that is RESOLVED now but was not previously observed as RESOLVED
      for (const updatedItem of updated.checklist.items) {
        if (!(updatedItem instanceof DocumentRequirement)) continue;
        if (updatedItem.status !== ChecklistItemStatus.Resolved) continue;

        const previousItem = previous?.checklist.items.find(
          (i) => i.id === updatedItem.id
        );
        if (previousItem?.status !== ChecklistItemStatus.Resolved) {
          this.documentSaved(updated.paxSessionId, updatedItem.itemType);
        }
      }

      // Emit paxReadyToFly if this pax is READY_TO_FLY now but was not previously observed as READY_TO_FLY
      if (
        previous?.checklist.resolutionStatus !==
          ChecklistResolutionStatus.ReadyToFly &&
        updated.checklist.resolutionStatus ===
          ChecklistResolutionStatus.ReadyToFly
      ) {
        this.paxReadyToFly(updated.paxSessionId, updated);
      }
    }

    // Emit allPaxReadyToFly if all passengers are now READY_TO_FLY
    // but not all were before (an empty list never counts as "all ready")
    const allNowReady =
      updatedPaxSessions.length > 0 &&
      updatedPaxSessions.every(
        (p) =>
          p.checklist.resolutionStatus === ChecklistResolutionStatus.ReadyToFly
      );
    const allPreviouslyReady =
      previousPaxSessions.length > 0 &&
      previousPaxSessions.every(
        (p) =>
          p.checklist.resolutionStatus === ChecklistResolutionStatus.ReadyToFly
      );

    if (allNowReady && !allPreviouslyReady) {
      this.allPaxReadyToFly(sessionId);
    }
  }

  // ── Internal ────────────────────────────────────────────────────────

  private send(type: BridgeEventType, payload: Record<string, unknown>): void {
    if (!this._isEmbedded) return;

    const message: BridgeMessage = {
      type,
      payload,
      version: BRIDGE_VERSION,
    };

    // iOS: pass the object directly
    if (window.webkit?.messageHandlers?.zamna) {
      try {
        window.webkit.messageHandlers.zamna.postMessage(message);
      } catch {
        // handler not available — swallow
      }
      return;
    }

    // Android: pass JSON string
    if (window.ZamnaBridge) {
      try {
        window.ZamnaBridge.postMessage(JSON.stringify(message));
      } catch {
        // bridge not available — swallow
      }
      return;
    }
  }
}

// ── Utilities ──────────────────────────────────────────────────────────────

function mapItemStatus(
  status: ChecklistItemStatus
): ChecklistEntry["status"] {
  switch (status) {
    case ChecklistItemStatus.Resolved:
      return "RESOLVED";
    case ChecklistItemStatus.VerificationRequired:
      return "VERIFICATION_REQUIRED";
    case ChecklistItemStatus.NotYetResolved:
      return "NOT_YET_RESOLVED";
  }
}

export default NativeBridge;
